<ng-template #loadingState>
  <div class="content-margin registration-page registration-page--loading">
    <p>Loading registration details...</p>
  </div>
</ng-template>

<ng-container *ngIf="!statusLoading; else loadingState">
  <div class="registration-confirmation-page" *ngIf="registrationRequest; else registrationForm">
    <section class="registration-confirmation" aria-live="polite">
      <div class="confirmation-summary">
        <img
          *ngIf="justSubmitted; else pendingIcon"
          class="confirmation-icon"
          src="assets/ontario-design-system/icons/svg/ontario-icon-alert-success.svg"
          alt=""
          aria-hidden="true"
        />
        <ng-template #pendingIcon>
          <svg class="confirmation-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M6 2l.01 6L10 12l-3.99 4.01L6 22h12v-6l-4-4 4-3.99V2H6zm10 14.5V20H8v-3.5l4-4 4 4z" />
          </svg>
        </ng-template>
        <h1 class="ewrs-page-title">{{ confirmationTitle }}</h1>
        <p class="confirmation-copy">{{ confirmationCopy }}</p>
      </div>

      <section class="registration-details" aria-labelledby="registrationDetailsTitle">
        <h2 class="ewrs-section-title" id="registrationDetailsTitle">Registration details</h2>
        <div class="registration-details-rule" aria-hidden="true"></div>

        <dl class="request-details">
          <div class="request-detail-row">
            <dt>Full name</dt>
            <dd>{{ registrationRequest.fullName || 'Not available' }}</dd>
          </div>
          <div class="request-detail-row">
            <dt>Email</dt>
            <dd>{{ registrationRequest.email || 'Not available' }}</dd>
          </div>
          <div class="request-detail-row">
            <dt>Reporting manager</dt>
            <dd>{{ registrationRequest.reportingManager || registrationRequest.managerEmail || 'Not available' }}</dd>
          </div>
          <div class="request-detail-row">
            <dt>Division</dt>
            <dd>{{ registrationRequest.division || 'Not available' }}</dd>
          </div>
          <div class="request-detail-row" *ngIf="registrationRequest.delegates?.length">
            <dt>Delegates</dt>
            <dd>
              <span *ngFor="let delegate of registrationRequest.delegates; let last = last">
                {{ delegate.name || delegate.email }}<span *ngIf="!last">, </span>
              </span>
            </dd>
          </div>
        </dl>
      </section>
    </section>
  </div>

  <ng-template #registrationForm>
    <div class="content-margin registration-page">
      <section class="registration-content">
        <p class="back-link-row">
          <a class="back-link" routerLink="/accessdenied">
            <span class="back-link-icon" aria-hidden="true"></span>
            Back to Registration
          </a>
        </p>

        <h1 class="ewrs-page-title">Employee Workspace Reservation System (EWRS) self-registration</h1>
        <p class="intro-copy">
          Please fill out the form below to register for EWRS. Once you submit your registration request, it will be
          sent to your selected manager for review. You'll receive an email notification when a decision has been made.
        </p>

        <div class="ontario-alert" *ngIf="generalError" role="alert">
          <span class="ontario-alert__icon" aria-hidden="true">
            <img src="assets/ontario-design-system/icons/svg/ontario-icon-alert-error.svg" alt="" />
          </span>
          <div class="ontario-alert__content">
            <h2 class="ewrs-section-title ontario-alert__title">There is a problem</h2>
            <p>{{ generalError }}</p>
          </div>
        </div>

        <form class="registration-form" (ngSubmit)="submit()" novalidate>
          <div class="registration-fields">
            <app-ods-input
              class="registration-field"
              [labelText]="'Reporting manager *'"
              [labelFor]="'reportingManagerSelect'"
              [labelId]="'reportingManagerLabel'"
            >
              <mat-form-field [class.field-error]="showManagerRequiredError || managerValidationError">
                <mat-select
                  id="reportingManagerSelect"
                  [(value)]="manager"
                  aria-labelledby="reportingManagerLabel"
                  placeholder="Select"
                  required
                  (selectionChange)="managerSelected()"
                >
                  <mat-option class="manager-search-option">
                    <ngx-mat-select-search
                      [ariaLabel]="'Search for manager'"
                      [formControl]="managerSearchControl"
                      [placeholderLabel]="'Search'"
                      [clearSearchInput]="false"
                      [showToggleAllCheckbox]="false"
                      [noEntriesFoundLabel]="managerNoResultsLabel"
                    ></ngx-mat-select-search>
                  </mat-option>

                  <mat-option *ngFor="let user of searchedManagers" [value]="user">
                    {{ user.name }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <div *ngIf="showManagerRequiredError" class="field-error-label">Select a reporting manager.</div>
            </app-ods-input>

            <div class="ontario-alert" *ngIf="managerValidationError" role="alert">
              <span class="ontario-alert__icon" aria-hidden="true">
                <img src="assets/ontario-design-system/icons/svg/ontario-icon-alert-error.svg" alt="" />
              </span>
              <div class="ontario-alert__content">
                <h2 class="ewrs-section-title ontario-alert__title">There is a problem</h2>
                <p>{{ managerValidationError }} Please contact {{ supportEmail }} or your manager to obtain access.</p>
              </div>
            </div>

            <app-ods-input
              class="registration-field"
              [labelText]="'Division *'"
              [labelFor]="'divisionSelect'"
              [labelId]="'divisionLabel'"
            >
              <mat-form-field [class.field-error]="showDivisionRequiredError">
                <mat-select
                  id="divisionSelect"
                  aria-label="Select your division"
                  [(value)]="currentDivision"
                  placeholder="Select"
                  required
                  (openedChange)="onDivisionOpened($event)"
                  [compareWith]="compareDivision"
                >
                  <mat-option>
                    <ngx-mat-select-search
                      [ariaLabel]="'Search for division'"
                      [formControl]="divisionSearchControl"
                      [placeholderLabel]="'Search'"
                      [clearSearchInput]="false"
                      [noEntriesFoundLabel]="divisionNoResultsLabel"
                    ></ngx-mat-select-search>
                  </mat-option>

                  <mat-option *ngFor="let division of divisions" [value]="division">
                    {{ division.name }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <div *ngIf="showDivisionRequiredError" class="field-error-label">Select a division.</div>
            </app-ods-input>

            <section class="delegate-section">
              <app-ods-input
                class="delegate-search-field"
                [labelText]="'My delegates'"
                [labelHint]="'(up to 5)'"
                [labelFor]="'delegateSelect'"
                [labelId]="'delegateSelectLabel'"
              >
                <div class="delegate-search-control">
                  <mat-form-field>
                    <input
                      matInput
                      id="delegateSelect"
                      type="text"
                      placeholder="Search"
                      aria-labelledby="delegateSelectLabel"
                      [formControl]="delegateSearchControl"
                      [matAutocomplete]="delegateAuto"
                      [disabled]="disableDelegate"
                      autocomplete="off"
                    />
                    <mat-autocomplete
                      #delegateAuto="matAutocomplete"
                      [displayWith]="displayDelegate"
                      (optionSelected)="delegateSelected($event.option.value)"
                    >
                      <mat-option
                        *ngIf="delegateSearchControl.value && !searchedDelegates?.length && delegateNoResultsLabel"
                        disabled
                      >
                        {{ delegateNoResultsLabel }}
                      </mat-option>
                      <mat-option *ngFor="let user of searchedDelegates" [value]="user" [attr.aria-label]="user.name">
                        {{ user.name }}
                      </mat-option>
                    </mat-autocomplete>
                  </mat-form-field>
                  <button
                    type="button"
                    mat-flat-button
                    color="primary"
                    class="delegate-add-button"
                    (click)="addDelegate()"
                    [disabled]="disableDelegate"
                  >
                    Add
                  </button>
                </div>
              </app-ods-input>

              <div *ngIf="delegateFeedback" class="delegate-feedback">{{ delegateFeedback }}</div>

              <div *ngIf="delegates?.length" class="delegate-pill-list">
                <div *ngFor="let delegate of delegates; let i = index" class="delegate-pill">
                  <span>{{ delegate.name }}</span>
                  <button
                    type="button"
                    class="clear-delegate"
                    [matTooltip]="'Remove ' + delegate.name"
                    [attr.aria-label]="'Remove ' + delegate.name"
                    (click)="removeDelegate(i)"
                  >
                    <span class="clear-delegate-icon" aria-hidden="true"></span>
                  </button>
                </div>
              </div>
            </section>
          </div>

          <div class="registration-declaration-group">
            <app-ods-checkbox
              class="registration-declaration"
              [checkboxId]="'selfRegistrationDeclarationCheckbox'"
              [checked]="isChecked"
              [error]="showDeclarationRequiredError"
              (checkedChange)="declarationChange($event)"
            >
              I hereby acknowledge and agree that I will review and abide by all procedures therein before accessing any
              workspace.
            </app-ods-checkbox>
            <div *ngIf="showDeclarationRequiredError" class="field-error-label declaration-error">
              Confirm that you have reviewed the declaration.
            </div>

            <button
              mat-flat-button
              color="primary"
              class="registration-submit-button"
              type="submit"
              [disabled]="submitting"
            >
              {{ submitting ? 'Submitting...' : 'Submit registration request' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  </ng-template>
</ng-container>
